import { Link, useLocation } from "react-router-dom";
import { ChevronRight } from "lucide-react";
import { useBreadcrumb } from "../context/BreadcrumbContext";

export default function Breadcrumbs() {
  const location = useLocation();
  const { dynamicLabels } = useBreadcrumb();

  const pathname = location.pathname;
  const segments = pathname.split("/").filter(Boolean);

  let currentPath = "";

  const renderBreadcrumb = (crumbs) => (
    <nav
      className="mb-1 text-slate-600 dark:text-slate-400"
      aria-label="Breadcrumb"
    >
      <ol className="flex flex-wrap items-center gap-1">
        {crumbs.map((crumb, index) => (
          <li key={index} className="flex items-center gap-1 max-w-full">
            {index !== 0 && (
              <ChevronRight
                size={14}
                className="text-slate-400 dark:text-slate-600 shrink-0"
              />
            )}

            {crumb.clickable ? (
              <Link
                to={crumb.path}
                className="truncate max-w-[8rem] sm:max-w-[12rem] md:max-w-none text-xs sm:text-sm md:text-base text-sky-700 dark:text-blue-400 hover:underline"
              >
                {crumb.label}
              </Link>
            ) : (
              <span className="truncate max-w-[9rem] sm:max-w-[14rem] md:max-w-none text-xs sm:text-sm md:text-base text-slate-700 dark:text-slate-200 font-medium">
                {crumb.label}
              </span>
            )}
          </li>
        ))}
      </ol>
    </nav>
  );

  /* KNOWLEDGE MANAGEMENT */

  const isKnowledge = pathname.startsWith("/knowledge-management");

  const isTopicAdd = pathname === "/knowledge-management/topics/add";

const isSubTopicAdd =
  pathname === "/knowledge-management/topics/add-sub-topic";

const topicEditMatch = pathname.match(
  /^\/knowledge-management\/topics\/([^/]+)\/edit$/
);

const subTopicEditMatch = pathname.match(
  /^\/knowledge-management\/topics\/([^/]+)\/([^/]+)\/edit-sub-topic$/
);


  if (isKnowledge) {
 let crumbs = [
  {
    label: "Knowledge Management",
    path: "/knowledge-management",
    clickable: true,
  },
];

if (pathname !== "/knowledge-management") {
  crumbs.push({
    label: "Topics",
    path: "/knowledge-management/topics",
    clickable: true,
  });
}


if (
  pathname.startsWith("/knowledge-management/topics/") &&
  dynamicLabels?.topicId &&
  dynamicLabels?.topicTitle &&
  !pathname.includes("/edit")
) {
  const isTopicPage = pathname === `/knowledge-management/topics/${dynamicLabels.topicId}`;

  crumbs.push({
    label: dynamicLabels.topicTitle,
    path: `/knowledge-management/topics/${dynamicLabels.topicId}`,
    clickable: !isTopicPage,
  });
}
if (isTopicAdd) {
  crumbs.push({
    label: "Add",
    clickable: false,
  });
}

if (isSubTopicAdd) {
  crumbs.push({
    label: "Add Sub Topic",
    clickable: false,
  });
}

if (topicEditMatch) {
crumbs.push({
  label: `Edit ${dynamicLabels?.topicTitle}`,
  clickable: false,
});
}

if (subTopicEditMatch) {
crumbs.push({
  label: `Edit ${dynamicLabels?.topicTitle}`,
  path: `/knowledge-management/topics/${dynamicLabels?.topicId}/edit`,
  clickable: true,
});

crumbs.push({
  label: `Edit ${dynamicLabels?.subTopicTitle}`,
  clickable: false,
});
}
  if (
  pathname.includes("/video/") &&
  dynamicLabels?.videoTitle
) {
  crumbs.push({
    label: dynamicLabels.videoTitle,
    clickable: false,
  });
}
    return renderBreadcrumb(crumbs);
  }

  /* ASSESSMENTS */

  if (
    segments.length === 3 &&
    segments[0] === "assessments" &&
    ["confirm", "start", "review"].includes(segments[1])
  ) {
    const action = segments[1];
    const assessmentId = segments[2];
    const name = dynamicLabels?.[assessmentId] || assessmentId;

    const actionLabel =
      action === "confirm"
        ? "Confirm"
        : action === "start"
        ? "Start"
        : "Review";

    const crumbs = [
      { label: "Assessments", path: "/assessments", clickable: true },
      { label: `${actionLabel} ${name}`, path: pathname, clickable: false },
    ];

    return renderBreadcrumb(crumbs);
  }

  /* QUIZZES */

  if (
    segments.length === 3 &&
    segments[0] === "quizzes" &&
    ["start", "review"].includes(segments[1])
  ) {
    const action = segments[1];
    const quizId = segments[2];
    const name = dynamicLabels?.[quizId] || quizId;

    const actionLabel = action === "start" ? "Start" : "Review";

    const crumbs = [
      { label: "Quizzes", path: "/quizzes", clickable: true },
      { label: `${actionLabel} ${name}`, path: pathname, clickable: false },
    ];

    return renderBreadcrumb(crumbs);
  }

  /* MASTERS ASSESSMENTS */

  if (segments.length >= 3 && segments[0] === "masters-assessments") {
    const assessmentId = segments[1];
    const assessmentLabel = dynamicLabels?.[assessmentId] || assessmentId;

    const basePath = `/masters-assessments/${assessmentId}`;

    if (segments[2] === "questions") {
      if (segments[3] === "import") {
        return renderBreadcrumb([
          { label: "Masters Assessment", path: "/masters-assessments", clickable: true },
          { label: `${assessmentLabel} Questions`, path: `${basePath}/questions`, clickable: true },
          { label: "Import", path: pathname, clickable: false },
        ]);
      }

      return renderBreadcrumb([
        { label: "Masters Assessment", path: "/masters-assessments", clickable: true },
        { label: `${assessmentLabel} Questions`, path: pathname, clickable: false },
      ]);
    }

    if (segments[2] === "sessions") {
      return renderBreadcrumb([
        { label: "Masters Assessment", path: "/masters-assessments", clickable: true },
        { label: `${assessmentLabel} Sessions`, path: pathname, clickable: false },
      ]);
    }

    if (segments[2] === "attendees") {
      if (segments.length === 4) {
        const attendeeId = segments[3];
        const attendeeLabel = dynamicLabels?.[attendeeId] || attendeeId;

        return renderBreadcrumb([
          { label: "Masters Assessment", path: "/masters-assessments", clickable: true },
          { label: `${assessmentLabel} Attendees`, path: `${basePath}/attendees`, clickable: true },
          { label: attendeeLabel, path: pathname, clickable: false },
        ]);
      }

      return renderBreadcrumb([
        { label: "Masters Assessment", path: "/masters-assessments", clickable: true },
        { label: `${assessmentLabel} Attendees`, path: pathname, clickable: false },
      ]);
    }
  }

  /* MASTERS QUIZZES */

  if (segments.length >= 3 && segments[0] === "masters-quizzes") {
    const quizId = segments[1];
    const quizLabel = dynamicLabels?.[quizId] || quizId;

    const basePath = `/masters-quizzes/${quizId}`;

    if (segments[2] === "questions") {
      if (segments[3] === "import") {
        return renderBreadcrumb([
          { label: "Masters Quiz", path: "/masters-quizzes", clickable: true },
          { label: `${quizLabel} Questions`, path: `${basePath}/questions`, clickable: true },
          { label: "Import", path: pathname, clickable: false },
        ]);
      }

      return renderBreadcrumb([
        { label: "Masters Quiz", path: "/masters-quizzes", clickable: true },
        { label: `${quizLabel} Questions`, path: pathname, clickable: false },
      ]);
    }

    if (segments[2] === "sessions") {
      return renderBreadcrumb([
        { label: "Masters Quiz", path: "/masters-quizzes", clickable: true },
        { label: `${quizLabel} Sessions`, path: pathname, clickable: false },
      ]);
    }

    if (segments[2] === "attendees") {
      if (segments.length === 4) {
        const attendeeId = segments[3];
        const attendeeLabel = dynamicLabels?.[attendeeId] || attendeeId;

        return renderBreadcrumb([
          { label: "Masters Quiz", path: "/masters-quizzes", clickable: true },
          { label: `${quizLabel} Attendees`, path: `${basePath}/attendees`, clickable: true },
          { label: attendeeLabel, path: pathname, clickable: false },
        ]);
      }

      return renderBreadcrumb([
        { label: "Masters Quiz", path: "/masters-quizzes", clickable: true },
        { label: `${quizLabel} Attendees`, path: pathname, clickable: false },
      ]);
    }
  }

  /* NORMAL ROUTES */

  const crumbs = segments.map((segment, index) => {
    currentPath += `/${segment}`;

    const label =
      dynamicLabels?.[segment] ||
      decodeURIComponent(segment)
        .replace(/-/g, " ")
        .replace(/\b\w/g, (c) => c.toUpperCase());

    return {
      label,
      path: currentPath,
      clickable: index !== segments.length - 1,
    };
  });

  return renderBreadcrumb(crumbs);
}